import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';

const countries = [
  {
    name: 'Singapore',
    context: 'Singapore is Eagentix\'s headquarters. We support MAS-regulated financial institutions, Smart Nation digital transformation programs, and enterprise cloud modernisation with private deployment options and PDPA compliance.',
  },
  {
    name: 'Malaysia',
    context: 'Eagentix supports Malaysian enterprises with private deployment options aligned with Malaysia\'s Digital Economy framework and Personal Data Protection Act (PDPA Malaysia) requirements.',
  },
  {
    name: 'Indonesia',
    context: 'Eagentix serves Indonesian enterprises with private infrastructure options compliant with Indonesia\'s Personal Data Protection Law (UU PDP), supporting digital transformation across finance, technology, and operations.',
  },
  {
    name: 'Philippines',
    context: 'Eagentix serves Philippine enterprises including BPO and financial services sectors, with agents for interview automation, recruiter workflows, and operational cost reduction under the Data Privacy Act of 2012.',
  },
  {
    name: 'Thailand',
    context: 'Eagentix is expanding in Thailand with enterprise AI agent deployments for cloud, finance, and HR teams, aligned with Thailand\'s PDPA framework.',
  },
  {
    name: 'Vietnam',
    context: 'Eagentix supports Vietnamese enterprises seeking AI-driven automation in finance, operations, and talent acquisition with region-appropriate governance and deployment options.',
  },
];

const useCases = [
  { title: 'Cloud Modernisation', desc: 'Migrate legacy infrastructure to multi-cloud environments with AI-guided architecture design and review.' },
  { title: 'Finance Automation', desc: 'Automate reconciliations, IFRS/GAAP reporting, KYC workflows, and financial operations across regional entities.' },
  { title: 'Talent Acquisition', desc: 'Scale interview automation, candidate screening, and recruiter workflows across large regional hiring programs.' },
  { title: 'Data Engineering', desc: 'Build data pipelines, govern schemas, and modernise data platforms across distributed SEA operations.' },
];

const faqs = [
  {
    q: 'Does Eagentix serve enterprises in Malaysia?',
    a: 'Yes. Eagentix deploys enterprise AI agents for Malaysian businesses including private deployment options aligned with Malaysia\'s Digital Economy framework and PDPA requirements.',
  },
  {
    q: 'Can Indonesian enterprises use Eagentix AI agents?',
    a: 'Yes. Eagentix supports Indonesian enterprise deployments with private infrastructure options compliant with Indonesia\'s Personal Data Protection Law (UU PDP).',
  },
  {
    q: 'Does Eagentix support AI agent deployment in the Philippines?',
    a: 'Yes. Eagentix serves Philippine enterprises including BPO and financial services sectors, with agents for interview automation, recruiter workflows, and operational cost reduction.',
  },
  {
    q: 'Is Eagentix available in Thailand and Vietnam?',
    a: 'Yes. Eagentix is expanding across ASEAN including Thailand and Vietnam, offering enterprise AI agent deployment with region-appropriate governance.',
  },
  {
    q: 'What languages do Eagentix agents support?',
    a: 'Eagentix agents currently operate primarily in English. Multi-language support for Bahasa Malaysia, Bahasa Indonesia, Filipino, and Thai is on the product roadmap.',
  },
];

export default function SoutheastAsiaAIAgents() {
  const sectionLight = { padding: '80px 0', background: '#f8fafc' };
  const sectionWhite = { padding: '80px 0', background: 'white' };
  const sectionDark = { padding: '80px 0', background: '#0f172a' };
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
  const card = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'white', padding: '28px 32px' };

  return (
    <>
      <SEO
        title="Enterprise AI Agents for Southeast Asia | ASEAN | Eagentix"
        description="Eagentix deploys enterprise AI agents across Southeast Asia — Singapore, Malaysia, Indonesia, Philippines, Thailand, and Vietnam — with private deployment and regional compliance governance."
        pathname="/industries/southeast-asia-ai-agents"
      />

      {/* Hero */}
      <section style={{ ...sectionDark, paddingTop: '120px' }}>
        <div style={container}>
          <div style={{ maxWidth: '800px' }}>
            <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Enterprise AI · Southeast Asia
            </span>
            <h1 style={{ fontSize: '3rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', margin: '16px 0 24px' }}>
              Enterprise AI Agents for Southeast Asia
            </h1>
            <p style={{ fontSize: '1.2rem', color: '#cbd5e1', lineHeight: 1.75, marginBottom: '36px' }}>
              Eagentix deploys enterprise AI agents across Southeast Asia — Singapore, Malaysia, Indonesia, Philippines, Thailand, and Vietnam — with private deployment options, multi-language support planning, and governance for regional compliance requirements.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <a
                href="https://app.eagentix.com/register"
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
              >
                Get Started
              </a>
              <a
                href="/#contact"
                style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                Talk to Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Countries served */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Countries Served
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Eagentix operates across six Southeast Asian markets with country-specific compliance and deployment awareness.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            {countries.map((c, i) => (
              <div key={i} style={card}>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>{c.name}</h3>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{c.context}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases in SEA */}
      <section style={sectionWhite}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Common Use Cases in Southeast Asia
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Enterprise teams across SEA are using Eagentix agents to automate high-value business functions.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
            {useCases.map((uc, i) => (
              <div key={i} style={card}>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0071e3', marginBottom: '10px' }}>{uc.title}</h3>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '40px' }}>
            Frequently Asked Questions
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {faqs.map((faq, i) => (
              <div key={i} style={card}>
                <h3 style={{ color: '#0071e3', fontSize: '1.05rem', fontWeight: 700, margin: '0 0 12px 0' }}>{faq.q}</h3>
                <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.75, margin: 0 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={sectionDark}>
        <div style={{ ...container, textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Deploy AI Agents Across Southeast Asia
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.75, marginBottom: '36px', maxWidth: '600px', margin: '0 auto 36px' }}>
            Get started with Eagentix enterprise AI agents. Our team will help you build a regional AI adoption roadmap.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://app.eagentix.com/register"
              target="_blank"
              rel="noopener noreferrer"
              style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
            >
              Start Free
            </a>
            <a
              href="/#contact"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              Book a Meeting
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
